import {
  type FocusEventHandler,
  type ReactElement,
  type Ref,
  forwardRef,
} from 'react';

import {
  Autocomplete,
  type AutocompleteCloseReason,
  type AutocompleteProps,
  type AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  type InputProps,
  TextField,
} from '@mui/material';
import type { Cancelable } from '@mui/utils/debounce';
import { ChevronDown } from 'lucide-react';

import { OptionalTooltip } from '@modules/_shared/components/OptionalTooltip';
import { useLocale } from '@shared/hooks/locale';

export type RaAutoCompleteCreatableItem<T> = {
  inputValue: string;
  new: true;
} & (T extends Record<any, any>
  ? T extends Array<any>
    ? {}
    : Partial<T>
  : {});

export const isCreatableItem = <T,>(
  item: T | RaAutoCompleteCreatableItem<T>,
): item is RaAutoCompleteCreatableItem<T> => (item as any).new;

export type RaAutocompleteProps<T, Multiple extends boolean = false> = {
  debouncedSearch: ((value: any) => void) & Cancelable;
  error?: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOptions: React.Dispatch<React.SetStateAction<T[]>>;
  getOptionLabel: (option: T) => string;
  InputProps?: InputProps;
  allowEmptySearch?: boolean;
  multiple?: Multiple;
  tooltip?: string;
  InputComponent?: React.FC<
    AutocompleteRenderInputParams & {
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
      onBlur?: FocusEventHandler<HTMLElement>;
    }
  >;
} & (
  | (Omit<
      AutocompleteProps<
        T | RaAutoCompleteCreatableItem<T>,
        Multiple,
        false,
        false
      >,
      'renderInput' | 'ref' | 'getOptionLabel' | 'renderOption' | 'multiple'
    > & {
      renderOption: (
        props: React.HTMLAttributes<HTMLLIElement> & { key: any },
        option: T | RaAutoCompleteCreatableItem<T>,
      ) => React.ReactNode;
      creatable:
        | {
            createItemFn?: (
              inputValue: string,
            ) => RaAutoCompleteCreatableItem<T>;
            elementPosition: 'first' | 'last';
          }
        | true;
    })
  | (Omit<
      AutocompleteProps<T, Multiple, false, false>,
      'renderInput' | 'ref' | 'getOptionLabel' | 'renderOption' | 'multiple'
    > & {
      renderOption: (
        props: React.HTMLAttributes<HTMLLIElement> & { key: any },
        option: T,
      ) => React.ReactNode;
      creatable?: never;
    })
);

const RaAutoCompleteWithRef = <T, Multiple extends boolean = false>(
  {
    sx,
    InputProps,
    open,
    setOpen,
    value,
    options,
    setOptions,
    loading,
    error,
    onChange,
    debouncedSearch,
    renderOption,
    getOptionLabel,
    creatable,
    allowEmptySearch = false,
    multiple,
    tooltip,
    InputComponent,
    fullWidth = true,
    onClose: onCloseProp,
    ...autoCompleteProps
  }: RaAutocompleteProps<T, Multiple>,
  ref: Ref<HTMLDivElement>,
) => {
  const { t } = useLocale();
  const createOptions =
    creatable == null
      ? null
      : {
          createItemFn:
            typeof creatable !== 'boolean' && creatable.createItemFn != null
              ? creatable.createItemFn
              : null,
          elementPosition:
            typeof creatable !== 'boolean' ? creatable.elementPosition : 'last',
        };
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value || allowEmptySearch) {
      debouncedSearch(event.target.value);
    } else {
      setOpen(false);
      setOptions([]);
    }
  };
  const handleBlur = autoCompleteProps.onBlur as
    | FocusEventHandler<HTMLElement>
    | undefined;

  const handleClose = (
    event: React.SyntheticEvent,
    reason: AutocompleteCloseReason,
  ) => {
    debouncedSearch.clear();
    setOpen(false);
    setOptions([]);
    onCloseProp?.(event, reason);
  };

  return (
    <Autocomplete<T, Multiple>
      sx={[
        ...(sx == null ? [] : Array.isArray(sx) ? sx : [sx]),
        {
          '& .MuiOutlinedInput-root': {
            overflow: 'hidden',
          },
        },
      ]}
      autoComplete={false}
      autoCorrect="off"
      open={open}
      clearOnBlur
      fullWidth={fullWidth}
      popupIcon={<ChevronDown size={16} style={{ display: 'block' }} />}
      onClose={handleClose}
      value={value}
      getOptionLabel={getOptionLabel}
      options={options}
      loading={loading}
      loadingText={t('Loading...')}
      noOptionsText={error ? t('Error') : t('No results')}
      onChange={onChange}
      filterOptions={(filteredResults, state) => {
        if (createOptions == null) {
          return filteredResults;
        }

        const createItem =
          createOptions.createItemFn?.(state.inputValue) ??
          ({
            inputValue: state.inputValue,
            new: true,
          } as RaAutoCompleteCreatableItem<T>);

        return createOptions.elementPosition === 'first'
          ? [createItem, ...filteredResults]
          : [...filteredResults, createItem];
      }}
      renderOption={renderOption}
      renderInput={params => (
        <OptionalTooltip title={tooltip} disabled={tooltip == null}>
          {InputComponent ? (
            <InputComponent
              {...params}
              onBlur={handleBlur}
              onChange={handleSearch}
              size="small"
              slotProps={{
                ...params.slotProps,
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  autoComplete: 'one-time-code', // disable autocomplete and autofill
                  type: 'new-password',
                  name: 'new-password',
                  autoCorrect: 'off',
                  spellCheck: 'false',
                },
                input: {
                  ...InputProps,
                  ...params.slotProps.input,
                  startAdornment: (
                    <>
                      {InputProps?.startAdornment}
                      {params.slotProps.input.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          flexShrink: 0,
                        }}
                      >
                        {loading ? (
                          <CircularProgress
                            color="inherit"
                            size={20}
                            disableShrink
                          />
                        ) : null}
                      </Box>
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          ) : (
            <TextField
              {...params}
              autoFocus={autoCompleteProps?.autoFocus}
              onBlur={handleBlur}
              onChange={handleSearch}
              variant="outlined"
              size="small"
              slotProps={{
                ...params.slotProps,

                input: {
                  ...InputProps,
                  ...params.slotProps.input,
                  sx: [
                    { background: 'var(--mui-palette-background-paper)' },
                    ...(InputProps?.sx == null
                      ? []
                      : Array.isArray(InputProps.sx)
                      ? InputProps.sx
                      : [InputProps.sx]),
                  ],
                  startAdornment: (
                    <>
                      {InputProps?.startAdornment}
                      {params.slotProps.input.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          flexShrink: 0,
                        }}
                      >
                        {loading ? (
                          <CircularProgress
                            color="inherit"
                            size={20}
                            disableShrink
                          />
                        ) : null}
                      </Box>
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },

                htmlInput: {
                  ...params.slotProps.htmlInput,
                  autoComplete: 'one-time-code', // disable autocomplete and autofill
                  type: 'new-password',
                  name: 'new-password',
                  autoCorrect: 'off',
                  spellCheck: 'false',
                },
              }}
            />
          )}
        </OptionalTooltip>
      )}
      ref={ref}
      multiple={multiple}
      {...(autoCompleteProps as any)}
      slotProps={{
        ...((autoCompleteProps as any).slotProps ?? {}),
        paper: {
          elevation: 0,
          ...((autoCompleteProps as any).slotProps?.paper ?? {}),
        },
      }}
    />
  );
};

export const RaAutoComplete = forwardRef(RaAutoCompleteWithRef) as <
  T,
  Multiple extends boolean = false,
>(
  props: RaAutocompleteProps<T, Multiple> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;
